import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

export interface DigitalNotaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: any[];
  preselectedDocId?: string | null;
  onCertificateIssued?: (certificate: any) => void;
  viewCertificateOnly?: any | null; // If opening directly to view an existing certificate
}

export function DigitalNotaryModal({
  isOpen,
  onClose,
  documents,
  preselectedDocId,
  onCertificateIssued,
  viewCertificateOnly = null
}: DigitalNotaryModalProps) {
  const { tenant } = useAuth();

  // Active Certificate View (if already notarized or just completed)
  const [activeCert, setActiveCert] = useState<any | null>(viewCertificateOnly);

  // Stepper: 1: Chọn tài liệu & Người ký, 2: Ký số, 3: Xác nhận & Đóng dấu, 4: Thành công / Chứng chỉ
  const [currentStep, setCurrentStep] = useState<number>(1);

  // Document selection
  const [selectedDocId, setSelectedDocId] = useState<string>(preselectedDocId || (documents[0]?.id ?? ''));
  const [docSearch, setDocSearch] = useState('');

  // Signer info
  const [signerName, setSignerName] = useState(tenant?.name || '');
  const [signerEmail, setSignerEmail] = useState(tenant?.email || '');
  const [signerRole, setSignerRole] = useState('Đại diện thẩm quyền');
  const [signingReason, setSigningReason] = useState('Phê duyệt hợp đồng & Cam kết pháp lý');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('Tài liệu đã được kiểm tra tính toàn vẹn và niêm phong mật mã trên sổ cái ErixOne.');

  // Signature mode: 'draw' | 'type'
  const [sigMode, setSigMode] = useState<'draw' | 'type'>('draw');
  const [inkColor, setInkColor] = useState<'#1d4ed8' | '#0f172a' | '#b91c1c'>('#1d4ed8');
  const [penSize, setPenSize] = useState<number>(3);
  const [typedName, setTypedName] = useState(tenant?.name || '');
  const [selectedFont, setSelectedFont] = useState<'cursive' | 'brush' | 'script'>('cursive');
  const [hasDrawn, setHasDrawn] = useState(false);

  // Canvas ref
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);

  // Loading & Submission
  const [submitting, setSubmitting] = useState(false);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [existingCerts, setExistingCerts] = useState<any[]>([]);
  const [copiedLink, setCopiedLink] = useState(false);

  // Reset or initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      if (viewCertificateOnly) {
        setActiveCert(viewCertificateOnly);
        setCurrentStep(4);
      } else {
        setActiveCert(null);
        setCurrentStep(1);
        if (preselectedDocId) {
          setSelectedDocId(preselectedDocId);
        } else if (documents.length > 0 && !selectedDocId) {
          setSelectedDocId(documents[0].id);
        }
      }
    }
  }, [isOpen, viewCertificateOnly, preselectedDocId, documents]);

  // Load existing certificates for selected document
  useEffect(() => {
    if (selectedDocId) {
      setLoadingCerts(true);
      api.getDocumentCertificates(selectedDocId)
        .then(res => setExistingCerts(res.certificates || []))
        .catch(() => setExistingCerts([]))
        .finally(() => setLoadingCerts(false));
    }
  }, [selectedDocId]);

  const selectedDoc = documents.find(d => d.id === selectedDocId);

  // ── Canvas Drawing Logic ──────────────────────────────────────────────────
  useEffect(() => {
    if (currentStep === 2 && sigMode === 'draw') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Handle high-DPI
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      ctx.scale(2, 2);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = inkColor;
      ctx.lineWidth = penSize;
    }
  }, [currentStep, sigMode, inkColor, penSize]);

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    const me = e as React.MouseEvent<HTMLCanvasElement>;
    return {
      x: me.clientX - rect.left,
      y: me.clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = inkColor;
    ctx.lineWidth = penSize;
    const { x, y } = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setHasDrawn(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  // Convert typed signature to an image data URL
  const generateTypedSignatureDataUrl = (): string => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 600;
    tempCanvas.height = 200;
    const ctx = tempCanvas.getContext('2d');
    if (!ctx) return '';

    ctx.fillStyle = 'transparent';
    ctx.fillRect(0, 0, 600, 200);

    ctx.fillStyle = inkColor;
    let fontName = 'cursive';
    if (selectedFont === 'brush') fontName = '"Brush Script MT", cursive';
    else if (selectedFont === 'script') fontName = '"Segoe Script", "Lucida Handwriting", cursive';
    else fontName = '"Snell Roundhand", cursive';

    ctx.font = `italic 48px ${fontName}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName || signerName || 'Erix User', 300, 100);

    return tempCanvas.toDataURL('image/png');
  };

  // ── Handle Final Certification Submission ────────────────────────────────
  const handleCertify = async () => {
    if (!selectedDocId) {
      alert('Vui lòng chọn tài liệu cần chứng thực số!');
      return;
    }
    if (!signerName.trim()) {
      alert('Vui lòng nhập họ tên người ký số!');
      return;
    }

    let finalSigData = '';
    if (sigMode === 'draw') {
      if (!hasDrawn || !canvasRef.current) {
        alert('Vui lòng vẽ chữ ký của bạn lên khung ký tên!');
        return;
      }
      finalSigData = canvasRef.current.toDataURL('image/png');
    } else {
      if (!typedName.trim()) {
        alert('Vui lòng nhập tên để tạo chữ ký nghệ thuật!');
        return;
      }
      finalSigData = generateTypedSignatureDataUrl();
    }

    setSubmitting(true);
    try {
      const reason = signingReason === 'Khác' ? customReason.trim() : signingReason;
      const res = await api.certifyDocument({
        document_id: selectedDocId,
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim(),
        signer_role: signerRole.trim(),
        signing_reason: reason || 'Chứng thực số & Niêm phong tài liệu',
        signature_data: finalSigData,
        signature_type: sigMode === 'draw' ? 'handwritten' : 'typed',
        notes: notes.trim()
      });

      if (res.success && res.certificate) {
        setActiveCert(res.certificate);
        setCurrentStep(4);
        if (onCertificateIssued) {
          onCertificateIssued(res.certificate);
        }
      } else {
        alert('Không thể hoàn tất chứng thực: ' + (res as any).error);
      }
    } catch (e: any) {
      alert('Lỗi ký số: ' + (e.message || 'Không thể kết nối đến Gateway'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyVerifyLink = () => {
    if (!activeCert) return;
    const verifyUrl = `${window.location.origin}/api/notary/verify/${activeCert.certificate_id}`;
    navigator.clipboard.writeText(verifyUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handlePrintCertificate = () => {
    window.print();
  };

  if (!isOpen) return null;

  // Filter documents for selector
  const filteredDocs = documents.filter(d => 
    !docSearch || d.filename.toLowerCase().includes(docSearch.toLowerCase())
  );

  return (
    <div className="modal-overlay" style={{ zIndex: 1100, backdropFilter: 'blur(8px)', background: 'rgba(3, 4, 12, 0.85)' }}>
      <div
        className="modal"
        style={{
          maxWidth: '920px',
          width: '95vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#0a0d1d',
          border: '1px solid rgba(56, 189, 248, 0.25)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8), 0 0 40px rgba(56, 189, 248, 0.1)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(90deg, rgba(30, 27, 75, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #0284c7 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 4px 15px rgba(2, 132, 199, 0.4)'
              }}
            >
              🛡️
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.3px' }}>
                  ErixOne Digital Notary — Chứng Thực Số & Đóng Dấu Blockchain
                </h2>
                <span className="badge badge--cyan" style={{ fontSize: '10px', padding: '2px 8px' }}>
                  RFC 3161 TSA
                </span>
              </div>
              <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                Ký số pháp lý, chứng thực dấu thời gian & neo giữ bằng chứng bất biến (Proof-of-Existence) trên Polygon PoS
              </p>
            </div>
          </div>
          <button className="modal__close" onClick={onClose} style={{ fontSize: '20px', padding: '6px 10px' }}>✕</button>
        </div>

        {/* Stepper Progress Bar (Only visible when creating a certificate) */}
        {!activeCert && (
          <div style={{ display: 'flex', background: 'rgba(15, 23, 42, 0.5)', borderBottom: '1px solid var(--border)', padding: '10px 24px', gap: '8px' }}>
            {[
              { num: 1, title: '1. Chọn Tài Liệu & Thông Tin' },
              { num: 2, title: '2. Ký Số (Signature Studio)' },
              { num: 3, title: '3. Thẩm Định & Đóng Dấu' },
            ].map(s => {
              const isActive = currentStep === s.num;
              const isDone = currentStep > s.num;
              return (
                <div
                  key={s.num}
                  onClick={() => isDone && setCurrentStep(s.num)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#38bdf8' : isDone ? '#10b981' : 'var(--text-muted)',
                    background: isActive ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
                    border: `1px solid ${isActive ? 'rgba(56, 189, 248, 0.3)' : 'transparent'}`,
                    cursor: isDone ? 'pointer' : 'default',
                    transition: 'all 0.2s'
                  }}
                >
                  <span
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: isDone ? '#10b981' : isActive ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                      color: isDone || isActive ? '#090a16' : '#94a3b8'
                    }}
                  >
                    {isDone ? '✓' : s.num}
                  </span>
                  <span>{s.title}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* ══════════════════════════════════════════════════════════════════
              BƯỚC 1: CHỌN TÀI LIỆU & THÔNG TIN NGƯỜI KÝ
             ══════════════════════════════════════════════════════════════════ */}
          {currentStep === 1 && !activeCert && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* 1.1 Khối Chọn Tài Liệu */}
              <div className="card" style={{ background: 'var(--bg-glass)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>📄</span> Chọn Tài Liệu Cần Cấp Chứng Thực Số
                  </div>
                  <input
                    type="text"
                    className="input"
                    placeholder="🔍 Tìm kiếm tài liệu..."
                    value={docSearch}
                    onChange={e => setDocSearch(e.target.value)}
                    style={{ width: '220px', padding: '5px 10px', fontSize: '12px' }}
                  />
                </div>

                {/* Dropdown danh sách tài liệu */}
                <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '4px' }}>
                  {filteredDocs.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                      Không tìm thấy tài liệu phù hợp.
                    </div>
                  ) : (
                    filteredDocs.map(doc => {
                      const isSelected = doc.id === selectedDocId;
                      const isNotarized = doc.metadata?.is_notarized || doc.is_notarized;
                      return (
                        <div
                          key={doc.id}
                          onClick={() => setSelectedDocId(doc.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: isSelected ? 'rgba(56, 189, 248, 0.15)' : 'rgba(255, 255, 255, 0.02)',
                            border: `1px solid ${isSelected ? 'var(--accent-cyan)' : 'var(--border)'}`,
                            transition: 'all 0.15s'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                            <span style={{ fontSize: '18px' }}>
                              {doc.source_type === 'pdf' ? '📕' : doc.source_type === 'xlsx' ? '📗' : doc.source_type === 'docx' ? '📘' : '📄'}
                            </span>
                            <div style={{ overflow: 'hidden' }}>
                              <div style={{ fontWeight: isSelected ? 600 : 500, color: '#f8fafc', fontSize: '13px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {doc.filename}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                {new Date(doc.created_at).toLocaleDateString('vi-VN')} • Phiên bản v{doc.version || 1}
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isNotarized && (
                              <span className="badge badge--green" style={{ fontSize: '10.5px' }}>
                                🛡️ Đã Ký Số
                              </span>
                            )}
                            <input
                              type="radio"
                              name="docSelector"
                              checked={isSelected}
                              onChange={() => setSelectedDocId(doc.id)}
                              style={{ cursor: 'pointer', accentColor: '#38bdf8' }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Selected Doc Summary Banner */}
                {selectedDoc && (
                  <div
                    style={{
                      marginTop: '14px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: 'rgba(56, 189, 248, 0.06)',
                      border: '1px solid rgba(56, 189, 248, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '12px'
                    }}
                  >
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Đang chọn: </span>
                      <strong style={{ color: '#f8fafc' }}>{selectedDoc.filename}</strong>
                    </div>
                    <div style={{ color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                      ID: {selectedDoc.id.slice(0, 16)}...
                    </div>
                  </div>
                )}
              </div>

              {/* 1.2 Khối Thông Tin Người Ký & Thẩm Quyền */}
              <div className="card" style={{ background: 'var(--bg-glass)', border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#f8fafc', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✍️</span> Thông Tin Người Ký & Mục Đích Chứng Thực
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                      Họ và tên người ký *
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={signerName}
                      onChange={e => { setSignerName(e.target.value); setTypedName(e.target.value); }}
                      placeholder="VD: Nguyễn Văn Eric"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                      Email định danh pháp lý *
                    </label>
                    <input
                      type="email"
                      className="input"
                      value={signerEmail}
                      onChange={e => setSignerEmail(e.target.value)}
                      placeholder="VD: eric@wowerix.com"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                      Chức vụ / Tư cách pháp nhân
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={signerRole}
                      onChange={e => setSignerRole(e.target.value)}
                      placeholder="VD: Tổng Giám Đốc, Trưởng phòng Pháp Chế"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                      Mục đích chứng thực *
                    </label>
                    <select
                      className="input"
                      value={signingReason}
                      onChange={e => setSigningReason(e.target.value)}
                    >
                      <option value="Phê duyệt hợp đồng & Cam kết pháp lý">📝 Phê duyệt hợp đồng & Cam kết pháp lý</option>
                      <option value="Xác nhận bản quyền & Tác quyền số">🛡️ Xác nhận bản quyền & Tác quyền số</option>
                      <option value="Nghiệm thu bàn giao dự án & Thanh toán">✅ Nghiệm thu bàn giao dự án & Thanh toán</option>
                      <option value="Niêm phong lưu trữ hồ sơ tuân thủ (Audit)">🔐 Niêm phong lưu trữ hồ sơ tuân thủ (Audit)</option>
                      <option value="Khác">✏️ Nhập lý do khác...</option>
                    </select>
                  </div>
                </div>

                {signingReason === 'Khác' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                      Lý do chứng thực cụ thể
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={customReason}
                      onChange={e => setCustomReason(e.target.value)}
                      placeholder="Nhập nội dung cam kết hoặc lý do chứng thực"
                    />
                  </div>
                )}

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                    Ghi chú / Cam kết tính trung thực
                  </label>
                  <textarea
                    className="input"
                    rows={2}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Lịch sử chứng thực cũ (nếu có) */}
              {existingCerts.length > 0 && (
                <div style={{ padding: '12px 16px', background: 'rgba(16, 185, 129, 0.08)', borderRadius: '10px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#34d399', marginBottom: '8px' }}>
                    📜 Tài liệu này đã có {existingCerts.length} chứng thực số trước đó:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {existingCerts.map((c, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                        <span>
                          <strong>{c.certificate_id}</strong> — Ký bởi: {c.signer_name} ({new Date(c.rfc3161_timestamp || c.created_at).toLocaleString('vi-VN')})
                        </span>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => { setActiveCert(c); setCurrentStep(4); }}
                          style={{ padding: '2px 8px', fontSize: '11px', color: '#38bdf8' }}
                        >
                          👁️ Xem Chứng Chỉ Này
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer Step 1 */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button className="btn btn--secondary" onClick={onClose}>Hủy bỏ</button>
                <button
                  className="btn btn--primary"
                  onClick={() => {
                    if (!selectedDocId) { alert('Vui lòng chọn 1 tài liệu'); return; }
                    if (!signerName.trim()) { alert('Vui lòng nhập họ tên người ký'); return; }
                    setCurrentStep(2);
                  }}
                >
                  Tiếp Tục: Ký Tên ➔
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              BƯỚC 2: SIGNATURE STUDIO (VẼ TAY HOẶC GÕ NGHỆ THUẬT)
             ══════════════════════════════════════════════════════════════════ */}
          {currentStep === 2 && !activeCert && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Signature Mode Selector */}
              <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
                <button
                  className={`btn ${sigMode === 'draw' ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                  onClick={() => setSigMode('draw')}
                >
                  ✍️ Vẽ Chữ Ký Tay (Mouse / Touch)
                </button>
                <button
                  className={`btn ${sigMode === 'type' ? 'btn--primary' : 'btn--secondary'} btn--sm`}
                  onClick={() => setSigMode('type')}
                >
                  🔤 Chữ Ký Nghệ Thuật (Type & Style)
                </button>
              </div>

              {/* Mode A: Canvas Vẽ Tay */}
              {sigMode === 'draw' ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                      Dùng chuột hoặc ngón tay để vẽ chữ ký của bạn vào khung bên dưới:
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Màu mực:</span>
                      {[
                        { color: '#1d4ed8', label: 'Xanh' },
                        { color: '#0f172a', label: 'Đen' },
                        { color: '#b91c1c', label: 'Đỏ' }
                      ].map(c => (
                        <button
                          key={c.color}
                          onClick={() => setInkColor(c.color as any)}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: c.color,
                            border: inkColor === c.color ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.3)',
                            cursor: 'pointer'
                          }}
                          title={`Mực ${c.label}`}
                        />
                      ))}
                      <button className="btn btn--secondary btn--sm" onClick={clearCanvas} style={{ marginLeft: '6px', fontSize: '11px', padding: '3px 8px' }}>
                        🗑️ Xóa ký lại
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      border: '2px dashed rgba(56, 189, 248, 0.4)',
                      borderRadius: '12px',
                      background: '#ffffff',
                      height: '220px',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.1)'
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      style={{ width: '100%', height: '100%', cursor: 'crosshair', touchAction: 'none' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                    {!hasDrawn && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#94a3b8',
                          pointerEvents: 'none',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        ✍️ Ký tên tại đây
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Mode B: Chữ Ký Nghệ Thuật */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' }}>
                      Tên thể hiện trên chữ ký
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={typedName}
                      onChange={e => setTypedName(e.target.value)}
                      placeholder="VD: Eric Nguyen"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {[
                      { id: 'cursive', name: 'Thư Pháp Cursive', sample: 'Snell' },
                      { id: 'brush', name: 'Nét Cọ Brush', sample: 'Brush' },
                      { id: 'script', name: 'Bút Máy Script', sample: 'Script' }
                    ].map(f => {
                      const isSelected = selectedFont === f.id;
                      return (
                        <div
                          key={f.id}
                          onClick={() => setSelectedFont(f.id as any)}
                          style={{
                            background: isSelected ? 'rgba(56, 189, 248, 0.15)' : 'var(--bg-glass)',
                            border: `1px solid ${isSelected ? 'var(--accent-cyan)' : 'var(--border)'}`,
                            borderRadius: '10px',
                            padding: '16px 12px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                          }}
                        >
                          <div
                            style={{
                              fontSize: '24px',
                              color: inkColor,
                              fontStyle: 'italic',
                              fontFamily: f.id === 'brush' ? '"Brush Script MT", cursive' : f.id === 'script' ? '"Segoe Script", cursive' : 'cursive',
                              minHeight: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {typedName || signerName || 'Chữ Ký Mẫu'}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{f.name}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Legal Warning Notice */}
              <div style={{ background: 'rgba(2, 132, 199, 0.08)', border: '1px solid rgba(2, 132, 199, 0.2)', padding: '12px 14px', borderRadius: '8px', fontSize: '11.5px', color: '#93c5fd' }}>
                ℹ️ <strong>Giá trị pháp lý:</strong> Chữ ký này sẽ được băm kết hợp với mã hash SHA-256 của tài liệu và ghi nhận vào Timestamp Token RFC 3161 cùng giao dịch Blockchain Proof-of-Existence không thể tẩy xóa hoặc phủ nhận.
              </div>

              {/* Footer Step 2 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button className="btn btn--secondary" onClick={() => setCurrentStep(1)}>
                  ⬅ Quay Lại
                </button>
                <button
                  className="btn btn--primary"
                  onClick={() => {
                    if (sigMode === 'draw' && !hasDrawn) {
                      alert('Vui lòng vẽ chữ ký của bạn trước khi tiếp tục!');
                      return;
                    }
                    if (sigMode === 'type' && !typedName.trim()) {
                      alert('Vui lòng nhập tên chữ ký!');
                      return;
                    }
                    setCurrentStep(3);
                  }}
                >
                  Tiếp Tục: Thẩm Định Mật Mã ➔
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              BƯỚC 3: THẨM ĐỊNH MẬT MÃ & ĐÓNG DẤU BLOCKCHAIN
             ══════════════════════════════════════════════════════════════════ */}
          {currentStep === 3 && !activeCert && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🔬</span> Xem Trước Tham Số Bằng Chứng Mật Mã (Cryptographic Audit)
              </div>

              {/* Audit Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', fontSize: '12.5px' }}>
                <div style={{ background: 'var(--bg-glass)', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase' }}>Tài liệu cấp chứng thực</div>
                  <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: '13.5px' }}>{selectedDoc?.filename}</div>
                  <div style={{ color: 'var(--accent-cyan)', fontSize: '11px', marginTop: '4px' }}>
                    Phiên bản: v{selectedDoc?.version || 1} • {selectedDoc?.source_type?.toUpperCase()}
                  </div>
                </div>

                <div style={{ background: 'var(--bg-glass)', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase' }}>Đại diện ký xác nhận</div>
                  <div style={{ fontWeight: 600, color: '#f8fafc' }}>{signerName}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '2px' }}>
                    {signerRole} ({signerEmail})
                  </div>
                </div>

                <div style={{ background: 'var(--bg-glass)', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase' }}>Mạng lưới Blockchain Neo Giữ</div>
                  <div style={{ fontWeight: 600, color: '#a855f7', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🟣</span> Polygon PoS Mainnet (Proof-of-Existence)
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px', fontFamily: 'var(--font-mono)' }}>
                    Registry: 0x71C8A36e65922c2F348F4f8d5598144D31698a9c
                  </div>
                </div>

                <div style={{ background: 'var(--bg-glass)', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--text-muted)', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase' }}>Chuẩn Dấu Thời Gian (TSA)</div>
                  <div style={{ fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⏱️</span> RFC 3161 Compliant Time-Stamp Authority
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                    Chính sách OID: 1.3.6.1.4.1.58923.1 (ErixOne TSA)
                  </div>
                </div>
              </div>

              {/* Purpose & Guarantee */}
              <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)', fontSize: '12px' }}>
                <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Mục đích ký & Cam kết:</div>
                <div style={{ color: '#cbd5e1', fontWeight: 500 }}>{signingReason === 'Khác' ? customReason : signingReason}</div>
                {notes && <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '4px' }}>"{notes}"</div>}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <button className="btn btn--secondary" onClick={() => setCurrentStep(2)} disabled={submitting}>
                  ⬅ Quay Lại Chỉnh Chữ Ký
                </button>
                <button
                  className="btn btn--primary"
                  onClick={handleCertify}
                  disabled={submitting}
                  style={{
                    background: 'linear-gradient(135deg, #0284c7 0%, #7c3aed 100%)',
                    padding: '10px 24px',
                    fontWeight: 700,
                    boxShadow: '0 4px 20px rgba(124, 58, 237, 0.4)'
                  }}
                >
                  {submitting ? (
                    <>
                      <span className="spinner" style={{ width: 14, height: 14 }} /> Đang Đóng Dấu Blockchain & Cấp Chứng Chỉ...
                    </>
                  ) : (
                    '🚀 Xác Nhận Ký Số & Đóng Dấu Blockchain'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              BƯỚC 4: GIẤY CHỨNG NHẬN SỐ HOÀN CHỈNH (CERTIFICATE DISPLAY)
             ══════════════════════════════════════════════════════════════════ */}
          {activeCert && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Top Banner */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  padding: '12px 18px',
                  borderRadius: '10px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '22px' }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#34d399', fontSize: '14px' }}>
                      Chứng Thực Số Đã Được Khởi Tạo & Xác Minh Thành Công!
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                      Mã chứng chỉ: <strong>{activeCert.certificate_id}</strong> • Trạng thái: Sổ cái Bất biến (Verified)
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn--secondary btn--sm" onClick={handleCopyVerifyLink}>
                    {copiedLink ? '✓ Đã Copy Link!' : '🔗 Copy Link Xác Thực'}
                  </button>
                  <button className="btn btn--primary btn--sm" onClick={handlePrintCertificate}>
                    🖨️ In / Tải PDF
                  </button>
                </div>
              </div>

              {/* 📜 THE OFFICIAL CERTIFICATE CARD (PRINTABLE) */}
              <div
                id="erixone-certificate-printable"
                style={{
                  background: 'linear-gradient(145deg, #0d1127 0%, #070914 100%)',
                  border: '2px solid rgba(234, 179, 8, 0.5)',
                  boxShadow: '0 0 35px rgba(234, 179, 8, 0.12), inset 0 0 50px rgba(0,0,0,0.5)',
                  borderRadius: '14px',
                  padding: '36px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Decorative Background Elements */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-60px',
                    right: '-60px',
                    width: '180px',
                    height: '180px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(234, 179, 8, 0.15) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  }}
                />

                {/* Certificate Header */}
                <div style={{ textAlign: 'center', borderBottom: '1px solid rgba(234, 179, 8, 0.25)', paddingBottom: '20px', marginBottom: '24px' }}>
                  <div style={{ fontSize: '12px', letterSpacing: '4px', textTransform: 'uppercase', color: '#fbbf24', fontWeight: 700 }}>
                    ERIXONE DIGITAL NOTARY AUTHORITY
                  </div>
                  <h1 style={{ fontSize: '26px', fontWeight: 800, margin: '8px 0 4px 0', color: '#ffffff', letterSpacing: '1px' }}>
                    GIẤY CHỨNG NHẬN SỐ & NIÊM PHONG PHÁP LÝ
                  </h1>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                    CERTIFICATE OF DIGITAL AUTHENTICITY & BLOCKCHAIN PROOF-OF-EXISTENCE
                  </div>
                  <div style={{ marginTop: '10px', display: 'inline-block', padding: '4px 14px', background: 'rgba(234, 179, 8, 0.12)', border: '1px solid #fbbf24', borderRadius: '20px', fontSize: '12px', fontWeight: 700, color: '#fef08a', fontFamily: 'var(--font-mono)' }}>
                    CERT ID: {activeCert.certificate_id}
                  </div>
                </div>

                {/* Certificate Body */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '28px', marginBottom: '28px' }}>
                  {/* Left Column: Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13px' }}>
                    <div>
                      <span style={{ color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tài liệu được chứng thực:</span>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc', marginTop: '2px' }}>
                        {activeCert.document_name}
                      </div>
                    </div>

                    <div>
                      <span style={{ color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Đại diện ký xác nhận:</span>
                      <div style={{ color: '#ffffff', fontWeight: 600 }}>
                        {activeCert.signer_name} {activeCert.signer_role && <span style={{ color: '#38bdf8' }}>({activeCert.signer_role})</span>}
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '11.5px' }}>{activeCert.signer_email}</div>
                    </div>

                    <div>
                      <span style={{ color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mục đích chứng thực:</span>
                      <div style={{ color: '#e2e8f0', fontWeight: 500 }}>{activeCert.signing_reason}</div>
                    </div>

                    <div>
                      <span style={{ color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mã băm toàn vẹn tài liệu (SHA-256 Digest):</span>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#38bdf8', wordBreak: 'break-all', background: 'rgba(0,0,0,0.4)', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)' }}>
                        {activeCert.content_hash}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Signature Stamp & Seal */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', borderLeft: '1px dashed rgba(234, 179, 8, 0.25)', paddingLeft: '24px' }}>
                    
                    {/* Visual Handwritten Signature */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px' }}>Chữ ký số đã xác thực</div>
                      <div
                        style={{
                          width: '180px',
                          height: '80px',
                          background: '#ffffff',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          border: '1px solid #e2e8f0',
                          padding: '6px'
                        }}
                      >
                        {activeCert.signature_data ? (
                          <img src={activeCert.signature_data} alt="Signature" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ color: '#000', fontStyle: 'italic', fontFamily: 'cursive', fontSize: '18px' }}>{activeCert.signer_name}</span>
                        )}
                      </div>
                    </div>

                    {/* Official Gold Seal Badge */}
                    <div
                      style={{
                        width: '90px',
                        height: '90px',
                        borderRadius: '50%',
                        border: '3px double #fbbf24',
                        background: 'radial-gradient(circle, #78350f 0%, #1e1b4b 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        boxShadow: '0 4px 15px rgba(251, 191, 36, 0.3)',
                        padding: '4px'
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>⚖️</span>
                      <span style={{ fontSize: '7.5px', fontWeight: 800, color: '#fef08a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ERIX VERIFIED</span>
                      <span style={{ fontSize: '6.5px', color: '#cbd5e1' }}>RFC 3161 TSA</span>
                    </div>

                  </div>
                </div>

                {/* Blockchain Proof Grid Footer */}
                <div
                  style={{
                    background: 'rgba(0,0,0,0.5)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '10px',
                    padding: '14px 18px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    fontSize: '11px'
                  }}
                >
                  <div>
                    <div style={{ color: '#94a3b8' }}>Mạng lưới Blockchain</div>
                    <div style={{ fontWeight: 600, color: '#a855f7' }}>{activeCert.blockchain_network || 'Polygon PoS'}</div>
                    <div style={{ color: '#64748b', fontSize: '10px' }}>Block #{activeCert.blockchain_block_number}</div>
                  </div>

                  <div>
                    <div style={{ color: '#94a3b8' }}>Dấu thời gian chuẩn RFC 3161</div>
                    <div style={{ fontWeight: 600, color: '#10b981' }}>
                      {new Date(activeCert.rfc3161_timestamp || activeCert.created_at).toLocaleString('vi-VN')}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '10px' }}>TSA Policy: {activeCert.rfc3161_tsa_policy}</div>
                  </div>

                  <div>
                    <div style={{ color: '#94a3b8' }}>Mã giao dịch Blockchain (TxHash)</div>
                    <div style={{ fontFamily: 'var(--font-mono)', color: '#38bdf8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={activeCert.blockchain_tx_hash}>
                      {activeCert.blockchain_tx_hash}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '10px' }}>Confirmed on-chain (12+ confirmations)</div>
                  </div>
                </div>

                {/* Tamper Warning */}
                <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '10.5px', color: '#64748b' }}>
                  Chứng thực này được mã hóa bảo đảm tính vẹn toàn tuyệt đối. Mọi sửa đổi trên nội dung file sẽ làm sai lệch hash SHA-256 và vô hiệu hóa chứng thực.
                </div>
              </div>

              {/* Actions Footer */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  className="btn btn--secondary"
                  onClick={() => {
                    setActiveCert(null);
                    setCurrentStep(1);
                  }}
                >
                  ✍️ Cấp Thêm Chứng Thực Khác
                </button>
                <button className="btn btn--primary" onClick={onClose}>
                  Hoàn Tất & Đóng
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
